import React, { useMemo } from 'react';
import { alpha, Box, Button, Grid, Typography, useTheme } from '@mui/material';
import { default as AddIcon } from '@mui/icons-material/Add';
import { default as DragIndicatorIcon } from '@mui/icons-material/DragIndicator';
import AddResourceButton from '../../../../common/AddResourceButton';
import { Room, RoomGroupType } from '../types';
import { getRoomsCounter } from '../utils';
import { generateDataTestId } from 'src/utils';

interface LevelButtonBarProps {
  canAddRooms: boolean;
  disabled?: boolean;
  handleProps?: any;
  isDragging?: boolean;
  onAddNewRoom: (type: string) => void;
  onAddRoom: () => void;
  rooms: Room[];
  level: Room | string;
}

const LevelButtonBar: React.FC<LevelButtonBarProps> = ({
  canAddRooms = false,
  disabled = false,
  handleProps,
  isDragging,
  onAddNewRoom,
  onAddRoom,
  rooms,
  level,
}) => {
  const theme = useTheme();
  const roomsCounter: RoomGroupType[] = useMemo(() => {
    return getRoomsCounter(rooms);
  }, [rooms]);

  const handlerCursor = useMemo(() => {
    return (disabled && 'default') || (isDragging && 'grabbing') || 'grab';
  }, [disabled, isDragging]);

  return (
    <Grid
      container
      sx={{
        pl: 0,
        pr: 0,
        mb: 0,
        flexDirection: 'column-reverse',
        [theme.breakpoints.up('sm')]: {
          flexDirection: 'row',
        },
      }}
    >
      <Grid
        display="flex"
        size={{
          xs: 12,
          sm: 8,
          md: 'grow',
        }}
      >
        <Grid
          container
          columnSpacing={1}
          rowSpacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          {roomsCounter.map((roomGroup) => {
            return (
              <Grid
                key={roomGroup.type}
                size={{
                  xs: 12,
                  sm: 'auto',
                }}
              >
                <AddResourceButton
                  dataProps={level}
                  disabled={!canAddRooms}
                  value={roomGroup.count ?? 0}
                  label={`${roomGroup.label}${roomGroup.count !== 1 ? 's' : ''}`}
                  onIncrement={() => onAddNewRoom(roomGroup.type)}
                />
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 4,
          md: 'auto',
        }}
      >
        <Grid
          container
          columnSpacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'end',
            flexDirection: 'row',
            [theme.breakpoints.up('sm')]: {
              flexDirection: 'row',
            },
          }}
        >
          <Grid>
            <Button
              data-testid={generateDataTestId(
                level.toString(),
                undefined,
                'addRoomButton',
              )}
              startIcon={<AddIcon />}
              variant="text"
              onClick={onAddRoom}
              sx={{
                textTransform: 'none',
                backgroundColor: 'transparent',
                color: 'secondary.main',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: 'secondary.main',
                  backgroundColor: ({ palette }) =>
                    alpha(palette.secondary.main, 0.04),
                },
                '&.Mui-disabled': {
                  backgroundColor: 'grey.300',
                  color: 'grey.500',
                },
              }}
              disabled={!canAddRooms}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Add Room
              </Typography>
            </Button>
          </Grid>
          <Grid
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box display="flex">
              <DragIndicatorIcon
                sx={{
                  color: disabled ? 'grey.300' : 'secondary.main',
                  cursor: handlerCursor,
                }}
                {...(disabled ? {} : handleProps?.listeners)}
                {...(disabled ? {} : handleProps?.attributes)}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default LevelButtonBar;
